import { useEffect, useMemo, useState, useRef } from 'react';
import { Sparkles, CheckCircle, AlertCircle, X } from 'lucide-react';
import { useUIStore } from '@/stores';
import { cn } from '@/lib/utils';
import type { GlobalGeneration } from '@/stores/uiStore';

export function getSimulatedProgress(gen: GlobalGeneration): number {
  if (gen.status === 'ready') return 100;
  if (gen.status === 'error') return 0;
  const elapsed = Date.now() - gen.startedAt;
  return Math.round(90 * (1 - Math.exp(-elapsed / gen.timeConstant)));
}

export function GenerationIndicator() {
  const { globalGenerations } = useUIStore();
  const [, setTick] = useState(0);

  const activeGenerations = globalGenerations.filter(
    (g) => g.status === 'generating'
  );

  useEffect(() => {
    if (activeGenerations.length === 0) return;
    const interval = setInterval(() => {
      setTick((t) => t + 1);
      // Safety: auto-expire stale generations
      const { globalGenerations: gens, updateGlobalGeneration: update } = useUIStore.getState();
      for (const gen of gens) {
        if (gen.status === 'generating') {
          const maxAge = gen.timeConstant * 4;
          if (Date.now() - gen.startedAt > maxAge) {
            update(gen.id, { status: 'error' });
          }
        }
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [activeGenerations.length]);

  if (activeGenerations.length === 0) return null;

  const totalProgress =
    activeGenerations.reduce((sum, g) => sum + getSimulatedProgress(g), 0) /
    activeGenerations.length;

  return (
    <div className="mx-3 mb-3">
      <div className="bg-background-tertiary border border-border-subtle rounded-xl p-3 space-y-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-accent-primary/10 flex items-center justify-center">
            <Sparkles size={16} className="text-accent-primary animate-pulse" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium">
              Generating {activeGenerations.length} item{activeGenerations.length > 1 ? 's' : ''}
            </div>
            <div className="text-xs text-foreground-muted">
              {Math.round(totalProgress)}% complete
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div
          className="h-1.5 bg-background-hover rounded-full overflow-hidden"
          role="progressbar"
          aria-valuenow={Math.round(totalProgress)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="h-full bg-gradient-to-r from-accent-primary to-accent-secondary transition-all duration-1000 rounded-full"
            style={{ width: `${totalProgress}%` }}
          />
        </div>

        {/* Individual items */}
        <div className="space-y-1 max-h-24 overflow-y-auto">
          {activeGenerations.map((gen) => (
            <div
              key={gen.id}
              className="flex items-center gap-2 text-xs text-foreground-muted"
            >
              <Sparkles size={12} className="flex-shrink-0" />
              <span className="truncate flex-1">{gen.label}</span>
              <span className="flex-shrink-0">{getSimulatedProgress(gen)}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function GenerationStatusToast() {
  const { globalGenerations, removeGlobalGeneration } = useUIStore();
  const removingRef = useRef<Set<string>>(new Set());

  const completedGenerations = useMemo(
    () => globalGenerations.filter((g) => g.status === 'ready' || g.status === 'error'),
    [globalGenerations]
  );

  useEffect(() => {
    completedGenerations.forEach((gen) => {
      if (removingRef.current.has(gen.id)) return;
      removingRef.current.add(gen.id);
      const delay = gen.status === 'ready' ? 2000 : 3000;
      setTimeout(() => {
        removeGlobalGeneration(gen.id);
        removingRef.current.delete(gen.id);
      }, delay);
    });
  }, [completedGenerations, removeGlobalGeneration]);

  if (completedGenerations.length === 0) return null;

  return (
    <div className="mx-3 mb-3 space-y-2">
      {completedGenerations.slice(0, 3).map((gen) => (
        <div
          key={gen.id}
          className={cn(
            'flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all',
            gen.status === 'ready'
              ? 'bg-green-500/10 border border-green-500/30'
              : 'bg-red-500/10 border border-red-500/30'
          )}
        >
          {gen.status === 'ready' ? (
            <CheckCircle size={14} className="text-green-500 flex-shrink-0" />
          ) : (
            <AlertCircle size={14} className="text-red-500 flex-shrink-0" />
          )}
          <span className="truncate flex-1 text-foreground-secondary">
            {gen.label}
          </span>
          <button
            onClick={() => removeGlobalGeneration(gen.id)}
            aria-label="Dismiss"
            className="p-0.5 hover:bg-background-hover rounded transition-colors"
          >
            <X size={12} className="text-foreground-muted" />
          </button>
        </div>
      ))}
    </div>
  );
}
